/**
 * @fileoverview Focused sections for the Change Password page.
 * @module pages/changePassword/ChangePasswordSections
 */
import { IconButton, SvgIcon } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import OtpInput from "react-otp-input";
import { ReactComponent as VisibilityIcon } from "../../assets/icons/Visibility.svg";
import { ReactComponent as VisibilityOffIcon } from "../../assets/icons/Visibility_off.svg";
import styles from "../../styles/ChangePasswordView.module.css";
import {
  OTP_INPUT_BORDER_ERROR,
  OTP_INPUT_BORDER_NORMAL,
  OTP_INPUT_BORDER_RADIUS,
  OTP_INPUT_COLOR_ERROR,
  OTP_INPUT_COLOR_NORMAL,
  OTP_INPUT_FONT_SIZE,
  OTP_NUM_INPUTS,
} from "./changePasswordConstants.js";

const getInputColor = (hasError) =>
  hasError ? OTP_INPUT_COLOR_ERROR : OTP_INPUT_COLOR_NORMAL;

const getInputBorder = (hasError) =>
  hasError ? OTP_INPUT_BORDER_ERROR : OTP_INPUT_BORDER_NORMAL;

export const ChangePasswordHeader = ({ t, userEmail, loading }) => (
  <div className={styles.modalHeader}>
    {loading ? (
      <>
        <span className={styles.title}>
          {t("pages.forgotPassword.verifyingCode")}
        </span>
        <CircularProgress />
      </>
    ) : (
      <>
        <span className={styles.title}>
          {t("pages.forgotPassword.verifyCodeSuccess")}
        </span>
        <span className={styles.subtitle}>
          {t("pages.forgotPassword.instructions01")}
          <strong>{userEmail}</strong>
          {t("pages.forgotPassword.instructions02")}
        </span>
      </>
    )}
  </div>
);

export const VerificationCodeSection = ({
  t,
  confirmationCode,
  setConfirmationCode,
  showCodeError,
  errorMessage,
  renderOtpInput,
  otpInputSize,
}) => (
  <div className={styles.otpContainer}>
    <span className={styles.subtitle}>
      {t("pages.forgotPassword.verifyCode")}
    </span>
    <OtpInput
      value={confirmationCode}
      onChange={setConfirmationCode}
      containerStyle={styles.otpInputContainer}
      inputStyle={{
        width: otpInputSize,
        height: otpInputSize,
        fontSize: OTP_INPUT_FONT_SIZE,
        border: getInputBorder(showCodeError),
        color: getInputColor(showCodeError),
        borderRadius: OTP_INPUT_BORDER_RADIUS,
      }}
      numInputs={OTP_NUM_INPUTS}
      renderInput={renderOtpInput}
    />
    {showCodeError && (
      <div className={styles.messageContainer}>
        <span className={styles.errorMessage}>
          {t(`errors.forgotPassword.${errorMessage}`)}
        </span>
      </div>
    )}
  </div>
);

const PasswordInput = ({
  label,
  value,
  showValue,
  hasError,
  onChange,
  onToggleVisibility,
}) => {
  const VisibilityComponent = showValue ? VisibilityOffIcon : VisibilityIcon;

  return (
    <div className={styles.passwordContainer}>
      <label className={styles.subtitle}>{label}</label>
      <div className={styles.inputContainer}>
        <input
          type={showValue ? "text" : "password"}
          onChange={onChange}
          value={value}
          style={{
            border: getInputBorder(hasError),
            color: getInputColor(hasError),
          }}
        />
        <IconButton
          onClick={onToggleVisibility}
          className={styles.visibilityButton}
        >
          <SvgIcon
            sx={{ color: getInputColor(hasError) }}
            component={VisibilityComponent}
          />
        </IconButton>
      </div>
    </div>
  );
};

export const PasswordFieldsSection = ({
  t,
  values,
  handlePasswordChange,
  handleConfirmPasswordChange,
  handleToggleShowPassword,
  handleToggleShowConfirmPassword,
}) => (
  <div className={styles.passwordsContainer}>
    <span className={styles.subtitle}>
      {t("pages.forgotPassword.newPassword")}
    </span>
    <PasswordInput
      label={t("pages.forgotPassword.createNewPassword")}
      value={values.password}
      showValue={values.showPassword}
      hasError={values.showPasswordError}
      onChange={handlePasswordChange}
      onToggleVisibility={handleToggleShowPassword}
    />
    <div className={styles.passwordRequirements}>
      <span className={styles.subtitle}>
        {t("pages.forgotPassword.requirementInstructions")}
      </span>
      <ul className={styles.list}>
        <li className={styles.listItem}>
          {t("pages.forgotPassword.requirements01")}
        </li>
        <li className={styles.listItem}>
          {t("pages.forgotPassword.requirements02")}
        </li>
      </ul>
    </div>
    <PasswordInput
      label={t("pages.forgotPassword.confirmPassword")}
      value={values.confirmPassword}
      showValue={values.showConfirmPassword}
      hasError={values.showPasswordError}
      onChange={handleConfirmPasswordChange}
      onToggleVisibility={handleToggleShowConfirmPassword}
    />
    {values.showPasswordError && (
      <div className={styles.messageContainer}>
        <span className={styles.errorMessage}>
          {t(`errors.forgotPassword.${values.errorMessage}`)}
        </span>
      </div>
    )}
  </div>
);

export const ChangePasswordActions = ({
  t,
  onCancel,
  onConfirm,
  confirmDisabled,
}) => (
  <div className={styles.buttonContainer}>
    <button className={styles.cancelButton} onClick={onCancel}>
      {t("pages.forgotPassword.cancelButton")}
    </button>
    <button
      className={styles.confirmButton}
      onClick={onConfirm}
      disabled={confirmDisabled}
    >
      {t("pages.forgotPassword.confirmNewPasswordButton")}
    </button>
  </div>
);
